/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — «UI nuova» on-screen keyboard height (MOBILE-NATIVE-PLAN W1.3).
 *              iOS Safari lays the keyboard OVER the page: fixed overlays and their
 *              sticky Save bars stayed under it. This publishes the covered height as
 *              `--nv-keyboard-inset` on <html> (+ `.nv-keyboard-open`) from
 *              visualViewport; nuova/_base.scss shrinks the overlays by it and hides the
 *              tab bar. Android with `interactive-widget=resizes-content` (set here on
 *              the viewport meta) shrinks the layout viewport itself: the inset reads 0
 *              there, so nothing is offset twice.
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      window.visualViewport, meta[name=viewport]
 *   siblings:  services/ui.js (installs when the nuova is on), nuova/_base.scss
 *   tests:     medical tests/unit/lib/keyboard-inset (unit)
 *   gotchas:   (1) A pinch zoom also shrinks visualViewport: only scale ≈ 1 counts as a
 *              keyboard. (2) Under 120px is browser chrome (toolbar collapse), not a
 *              keyboard. (3) The viewport meta keeps every existing directive (zoom
 *              lock included); only interactive-widget is added, and removed again on
 *              uninstall.
 */
const VAR = '--nv-keyboard-inset';
const OPEN_CLASS = 'nv-keyboard-open';
const MIN_KEYBOARD_PX = 120;
const WIDGET = 'interactive-widget=resizes-content';
let installed = false;
let frame = null;
let metaOriginal = null;

/** The keyboard height a visualViewport leaves uncovered at the bottom (0 when none). */
function keyboardInset(viewport = window.visualViewport, layoutHeight = window.innerHeight) {
  if (!viewport) return 0;
  if (Math.abs((viewport.scale ?? 1) - 1) > 0.01) return 0;
  const covered = Math.round(layoutHeight - viewport.height - viewport.offsetTop);
  return covered >= MIN_KEYBOARD_PX ? covered : 0;
}
function apply() {
  frame = null;
  const html = document.documentElement;
  const inset = keyboardInset();
  html.style.setProperty(VAR, `${inset}px`);
  html.classList.toggle(OPEN_CLASS, inset > 0);
}
function schedule() {
  if (frame !== null) return;
  frame = window.requestAnimationFrame(apply);
}
function addWidgetDirective() {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const content = meta.getAttribute('content') ?? '';
  if (content.includes('interactive-widget')) return;
  metaOriginal = content;
  meta.setAttribute('content', content ? `${content}, ${WIDGET}` : WIDGET);
}
function restoreWidgetDirective() {
  if (metaOriginal === null) return;
  document.querySelector('meta[name="viewport"]')?.setAttribute('content', metaOriginal);
  metaOriginal = null;
}

/** Starts tracking the keyboard (idempotent). */
function installKeyboardInset() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  addWidgetDirective();
  const viewport = window.visualViewport;
  if (!viewport) return;
  viewport.addEventListener('resize', schedule);
  viewport.addEventListener('scroll', schedule);
  apply();
}

/** Stops tracking and clears the published inset. */
function uninstallKeyboardInset() {
  if (!installed) return;
  installed = false;
  restoreWidgetDirective();
  window.visualViewport?.removeEventListener('resize', schedule);
  window.visualViewport?.removeEventListener('scroll', schedule);
  if (frame !== null) window.cancelAnimationFrame(frame);
  frame = null;
  const html = document.documentElement;
  html.style.removeProperty(VAR);
  html.classList.remove(OPEN_CLASS);
}

export { installKeyboardInset, keyboardInset, uninstallKeyboardInset };
//# sourceMappingURL=keyboard-inset.js.map
